import { Link } from 'react-router';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  Field,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useQuestionAccess } from '@/features/patient-questions/access';
import {
  useCloseQuestion,
  usePatientQuestion,
  useReplyQuestion,
  useRouteQuestion,
} from '@/features/patient-questions/api';
import {
  QUESTION_LIMITS,
  type StaffQuestion,
} from '@/features/patient-questions/types';
import { useBranchDoctors } from '@/features/prescription-requests/api';
import { isApiError } from '@/lib/api';
import {
  flagLabel,
  flagTone,
  QUESTION_ROUTE_REASON_LABEL,
  QUESTION_STATUS_LABEL,
  QUESTION_STATUS_TONE,
  resultValue,
} from './question-format';
import { when } from './rx-request-format';
import styles from './PatientQuestions.module.css';

/** Staff wording for the API's codes; the API's own message is shown under it. */
const ERRORS: Record<string, string> = {
  QUESTION_NOT_FOUND:
    'This question is no longer in your inbox. It may have been routed to another doctor.',
  QUESTION_NOT_ROUTED_TO_YOU:
    'This question is routed to another doctor. The clinic manager can route it to you.',
  QUESTION_CLOSED: 'This question is already closed. It has been reloaded.',
  QUESTION_CHANGED:
    'This question changed after you opened it (a new message, reply or routing). It has been reloaded: check it and try again.',
  DOCTOR_NOT_AT_BRANCH: 'Choose an active doctor of the question’s branch.',
};

/** Codes after which the question on screen is out of date. */
const RELOAD = new Set(['QUESTION_CHANGED', 'QUESTION_CLOSED']);

interface Failure {
  title: string;
  detail?: string;
  requestId?: string;
}

/**
 * One question from the app (RL-1139): the result it is about, the thread
 * and the reply (doctor), close, and routing (manager). Every action sends
 * the `version` read; on 409 QUESTION_CHANGED / QUESTION_CLOSED the question
 * is reloaded and the typed reply is kept.
 */
export function PatientQuestionDrawer({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const q = usePatientQuestion(id);
  const notFound = isApiError(q.error) && q.error.code === 'QUESTION_NOT_FOUND';

  return q.data ? (
    <QuestionDialog
      c={q.data}
      reloading={q.isFetching}
      reload={() => void q.refetch()}
      onClose={onClose}
    />
  ) : (
    <Dialog open onClose={onClose} title="Question from the app">
      {q.isLoading ? (
        <LoadingState label="Loading the question" />
      ) : (
        <ErrorState
          error={q.error}
          title={notFound ? ERRORS.QUESTION_NOT_FOUND : undefined}
          onRetry={notFound ? null : () => void q.refetch()}
        />
      )}
    </Dialog>
  );
}

function QuestionDialog({
  c,
  reloading,
  reload,
  onClose,
}: {
  c: StaffQuestion;
  reloading: boolean;
  reload: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const formId = useId();
  const access = useQuestionAccess();
  const reply = useReplyQuestion(c.id);
  const close = useCloseQuestion(c.id);
  const [confirmClose, setConfirmClose] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [text, setText] = useState('');
  const [textError, setTextError] = useState<string>();
  const isOpen = c.status !== 'closed';
  const canAnswer = access.canAnswer && c.textShown && isOpen;
  const busy = reply.isPending || close.isPending;

  function fail(err: unknown) {
    if (!isApiError(err)) {
      setFailure({ title: 'Could not reach the server. Try again.' });
      return;
    }
    const field = err.fieldError('text');
    if (field) {
      setTextError(field);
      return;
    }
    const known = ERRORS[err.code];
    setFailure({
      title: known ?? err.message,
      detail: known && err.message !== known ? err.message : undefined,
      requestId: err.requestId,
    });
    if (RELOAD.has(err.code)) {
      setConfirmClose(false);
      reload();
    }
  }

  async function send(andClose: boolean) {
    setFailure(null);
    const body = text.trim();
    if (!body) return setTextError('Write a reply for the patient.');
    try {
      await reply.mutateAsync({
        text: body,
        version: c.version,
        ...(andClose && { close: true }),
      });
      setText('');
      toast.show({
        tone: 'success',
        title: andClose
          ? 'Reply sent and question closed'
          : 'Reply sent: the patient reads it in the app',
      });
    } catch (err) {
      fail(err);
    }
  }

  async function doClose() {
    setFailure(null);
    try {
      await close.mutateAsync({ version: c.version });
      setConfirmClose(false);
      toast.show({ tone: 'success', title: 'Question closed' });
    } catch (err) {
      fail(err);
    }
  }

  const footer = confirmClose ? (
    <>
      <Button onClick={() => setConfirmClose(false)}>Cancel</Button>
      <Button
        variant="danger"
        loading={close.isPending}
        onClick={() => void doClose()}
      >
        Close question
      </Button>
    </>
  ) : canAnswer ? (
    <>
      <Button onClick={() => setConfirmClose(true)} disabled={busy}>
        Close without reply
      </Button>
      <Button onClick={() => void send(true)} disabled={busy}>
        Send and close
      </Button>
      <Button
        type="submit"
        form={formId}
        variant="primary"
        loading={reply.isPending}
      >
        Send reply
      </Button>
    </>
  ) : (
    <Button onClick={onClose}>Close</Button>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!confirmClose && !text.trim()}
      title={`Question · ${c.patient.fullName}`}
      footer={footer}
    >
      <div className={styles.drawer}>
        {failure && (
          <div className={styles.error} role="alert">
            {failure.title}
            {failure.detail && <span>{failure.detail}</span>}
            {failure.requestId && (
              <span>{t('common.requestId', { id: failure.requestId })}</span>
            )}
          </div>
        )}
        {reloading && <p className={styles.muted}>Reloading the question…</p>}

        <Summary c={c} />
        <ResultCard c={c} />

        {!c.textShown ? (
          <p className={styles.oversightNote}>
            {access.canReadInbox
              ? 'This question is routed to another doctor: its messages and the result value are shown only to that doctor.'
              : 'Oversight view: the messages and the result value are shown only to the doctor who answers this question.'}
          </p>
        ) : (
          <Thread c={c} />
        )}

        {confirmClose && (
          <div className={styles.confirm} role="alert">
            <strong>Close this question?</strong>
            <span>
              Closing is final: the patient cannot add to this thread, but can
              ask a new question about the result in the app.
            </span>
          </div>
        )}

        {canAnswer && !confirmClose && (
          <form
            id={formId}
            className={styles.form}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void send(false);
            }}
          >
            <Field
              label="Reply to the patient"
              hint={`The patient reads this in the app; the notification only says there is an update. Up to ${QUESTION_LIMITS.messageMax} characters.`}
              error={textError}
            >
              <textarea
                className={styles.textarea}
                value={text}
                maxLength={QUESTION_LIMITS.messageMax}
                onChange={(e) => {
                  setText(e.target.value);
                  setTextError(undefined);
                }}
              />
            </Field>
          </form>
        )}

        {access.canRoute && isOpen && !confirmClose && (
          <RouteForm c={c} onError={fail} clear={() => setFailure(null)} />
        )}
      </div>
    </Dialog>
  );
}

function Summary({ c }: { c: StaffQuestion }) {
  return (
    <dl className={styles.kv}>
      <dt>Status</dt>
      <dd>
        <Badge tone={QUESTION_STATUS_TONE[c.status]} dot>
          {QUESTION_STATUS_LABEL[c.status]}
        </Badge>
      </dd>
      <dt>Patient</dt>
      <dd>
        <Link to={`/patients/${c.patient.id}`}>{c.patient.fullName}</Link>
      </dd>
      <dt>Branch</dt>
      <dd>{c.branch.name}</dd>
      <dt>Asked</dt>
      <dd>{when(c.createdAt)}</dd>
      {c.waitingSince && (
        <>
          <dt>Waiting since</dt>
          <dd>{when(c.waitingSince)}</dd>
        </>
      )}
      <dt>Routed to</dt>
      <dd>
        {c.routedTo?.fullName ?? 'Branch doctors'}{' '}
        <span className={styles.muted}>
          ({QUESTION_ROUTE_REASON_LABEL[c.routeReason] ?? c.routeReason})
        </span>
      </dd>
      {c.answeredAt && (
        <>
          <dt>Last reply</dt>
          <dd>{when(c.answeredAt)}</dd>
        </>
      )}
      {c.closedAt && (
        <>
          <dt>Closed</dt>
          <dd>
            {when(c.closedAt)}
            {c.closedBy && ` · ${c.closedBy.fullName}`}
          </dd>
        </>
      )}
    </dl>
  );
}

/** The released result the question is about (value for the doctor only). */
function ResultCard({ c }: { c: StaffQuestion }) {
  const s = c.subject;
  if (!s) return null;
  const r = s.result;
  return (
    <section className={styles.result} aria-label="The result asked about">
      <h3 className={styles.section}>About this result</h3>
      <p className={styles.resultTitle}>
        <strong>{s.testName}</strong>
        {s.orderNumber && (
          <span className={`${styles.muted} mono`}>{s.orderNumber}</span>
        )}
      </p>
      {r && (
        <dl className={styles.kv}>
          <dt>Result</dt>
          <dd>
            {resultValue(c)}{' '}
            {r.flag && (
              <Badge tone={flagTone(r.flag)}>{flagLabel(r.flag)}</Badge>
            )}
          </dd>
          {r.referenceRange && (
            <>
              <dt>Reference range</dt>
              <dd>{r.referenceRange}</dd>
            </>
          )}
          {r.releasedAt && (
            <>
              <dt>Released</dt>
              <dd>{when(r.releasedAt)}</dd>
            </>
          )}
        </dl>
      )}
    </section>
  );
}

function Thread({ c }: { c: StaffQuestion }) {
  const messages = c.messages ?? [];
  return (
    <section aria-label="Conversation">
      <h3 className={styles.section}>Conversation</h3>
      {messages.length ? (
        <ol className={styles.thread}>
          {messages.map((m) => (
            <li
              key={m.id}
              className={
                m.authorType === 'patient'
                  ? styles.fromPatient
                  : styles.fromStaff
              }
            >
              <span className={styles.author}>
                {m.authorType === 'patient'
                  ? `Patient · ${c.patient.fullName}`
                  : `Clinic · ${m.staff?.fullName ?? 'Staff'}`}
                <time dateTime={m.sentAt}>{when(m.sentAt)}</time>
              </span>
              <p>{m.text}</p>
            </li>
          ))}
        </ol>
      ) : (
        <p className={styles.muted}>No messages.</p>
      )}
    </section>
  );
}

function RouteForm({
  c,
  onError,
  clear,
}: {
  c: StaffQuestion;
  onError: (err: unknown) => void;
  clear: () => void;
}) {
  const toast = useToast();
  const doctors = useBranchDoctors(c.branch.id, true);
  const route = useRouteQuestion(c.id);
  const current = c.routedTo?.id ?? '';
  const [doctorId, setDoctorId] = useState(current);
  const options = [
    { value: '', label: 'Any doctor of the branch' },
    ...(doctors.data ?? []).map((d) => ({ value: d.id, label: d.fullName })),
    // Keep the current doctor selectable even when not in the list.
    ...(c.routedTo && !doctors.data?.some((d) => d.id === c.routedTo!.id)
      ? [{ value: c.routedTo.id, label: c.routedTo.fullName }]
      : []),
  ];

  async function send() {
    clear();
    try {
      const saved = await route.mutateAsync({
        doctorId: doctorId || null,
        version: c.version,
      });
      toast.show({
        tone: 'success',
        title: saved.routedTo
          ? `Routed to ${saved.routedTo.fullName}`
          : 'Sent back to the branch’s doctors',
      });
    } catch (err) {
      onError(err);
    }
  }

  return (
    <form
      className={styles.route}
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <h3 className={styles.section}>Route to a doctor</h3>
      <div className={styles.routeRow}>
        <Field
          label="Doctor"
          hint={
            doctors.isError
              ? 'Could not load the branch’s doctors.'
              : 'A doctor of the question’s branch answers it.'
          }
        >
          <Select
            value={doctorId}
            options={options}
            disabled={doctors.isLoading}
            onChange={(e) => setDoctorId(e.target.value)}
          />
        </Field>
        {/* RL-1828 (gap #151): Route only once another doctor is picked. */}
        {doctorId !== current && (
          <Button type="submit" loading={route.isPending}>
            Route
          </Button>
        )}
      </div>
    </form>
  );
}
